/**
 * Availability Information Component
 *
 * @module @features/departments/components/AvailabilityInfo
 */

import React from 'react';
import { useI18n } from '@shared/i18n';
import type { Employee } from '@shared/types';
import { NameAndValue } from './NameAndValue';

/**
 * Props for AvailabilityInfo component
 */
export interface AvailabilityInfoProps {
    employee: Employee | undefined;
}

/**
 * Displays employee availability information
 */
export function AvailabilityInfo({
    employee,
}: AvailabilityInfoProps): React.ReactElement {
    const { t } = useI18n();

    return (
        <>
            <NameAndValue
                name={t('departments.availability.availableForShifts')}
                value={
                    employee?.enabled
                        ? t('departments.boolean.yes')
                        : t('departments.boolean.no')
                }
            />
            <NameAndValue
                name={t('departments.availability.worksOnlyWeekend')}
                value={
                    employee?.worksOnlyWeekend
                        ? t('departments.boolean.yes')
                        : t('departments.boolean.no')
                }
            />
            <NameAndValue
                name={t('departments.availability.f3Eligible')}
                value={
                    employee?.f3Eligible
                        ? t('departments.boolean.yes')
                        : t('departments.boolean.no')
                }
            />
            <NameAndValue
                name={t('departments.availability.sundaysMaxSequence')}
                value={employee?.sundaysMaxSequence}
            />
            <NameAndValue
                name={t('departments.availability.quickReturnsWeekend')}
                value={employee?.nrOfQuickReturnsPerWeekOnWeekend ?? 0}
            />
            <NameAndValue
                name={t('departments.availability.quickReturnsWeekdays')}
                value={employee?.nrOfQuickReturnsPerWeekOnWeekdays ?? 0}
            />
            <NameAndValue
                name={t('departments.availability.redDaysMaxSequence')}
                value={employee?.redDaysMaxSequence}
            />
            <NameAndValue
                name={t('departments.availability.movableHolidaysMax')}
                value={employee?.movableHolidaysMaxPerTurnus}
            />
            <NameAndValue
                name={t('departments.availability.restCompensationEligible')}
                value={
                    employee?.restCompensationEligible
                        ? t('departments.boolean.yes')
                        : t('departments.boolean.no')
                }
            />
        </>
    );
}
